import { createContext, useContext, useState } from "react";
import { authService } from "app/services";
import { useMutation, useQuery } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { Spinner } from "app/components";

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [token, setToken] = useState(() =>
    window.localStorage.getItem("token")
  );

  const [isAuthenticated, setAuthenticated] = useState(!!token);

  const tokenQuery = useQuery({
    queryKey: ["get:check-token", token],
    queryFn: () => authService.checkToken({ accessToken: token }),
    onSuccess: () => {
      setAuthenticated(true);
    },
    onError: async () => {
      setAuthenticated(false);

      return await authService.logout();
    },
    enabled: !!token,
  });

  const loginMutation = useMutation({
    queryKey: ["login"],
    mutationFn: ({ username, password }) =>
      authService.login({ username, password }),
    onSuccess: (data) => {
      window.localStorage.setItem("token", data.token);

      setToken(data.token);

      setAuthenticated(true);

      return toast.success("Sesión iniciada");
    },
    onError: (error) => {
      return toast.error(error.response.statusText || error.message);
    },
  });

  const logoutMutation = useMutation({
    queryKey: ["logout"],
    mutationFn: authService.logout,
    onSuccess: () => {
      setAuthenticated(false);

      return toast.success("Sesión cerrada");
    },
  });

  async function login({ user = {} }) {
    return await loginMutation.mutateAsync(user);
  }

  async function logout() {
    const confirmed = window.confirm("¿Desea cerrar sesión?");

    if (!!confirmed) {
      return await logoutMutation.mutateAsync();
    }

    return false;
  }

  if (!!tokenQuery.isLoading && !!token) {
    return <Spinner label="Autenticando..." />;
  }

  return (
    <AuthContext.Provider
      value={{
        token,
        isAuthenticated,
        login,
        logout,
        isLogging: loginMutation.isLoading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuthContext() {
  const authContext = useContext(AuthContext);

  if (!authContext) {
    throw new Error("useAuthContext should be used inside the AuthProvider.");
  }

  return authContext;
}
